import Modal from '@material-ui/core/Modal';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { BaseClickable, Box, palette, Spinner, Stack, Text } from '@solvhealth/jigsaw-v1';
import Location from '@solvhealth/types/interfaces/Location';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { setReasonForVisit } from '~/actions/newBooking';
import {
  useVisitSummary,
  useVisitSummaryDownloadedMutation,
} from '~/components/AccountGetsMarried/components/GetCare/hooks/useVisitSummary';
import { VisitSummaryStatuses } from '~/components/AccountGetsMarried/constants/visitSummary';
import { colors } from '~/constants/colors';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  ACCOUNT_PROFILE_PAST_VISITS_CARD,
  USER_PROFILE_PAST_APPOINTMENT_CARD,
  VISIT_SUMMARY_TOUCH_DOWNLOAD,
  VISIT_SUMMARY_TOUCH_VIEW,
} from '~/core/analytics/events';
import { useRebooking } from '~/hooks/useRebooking';
import BookingProfileForm from '../../../../components/CDPBookingForm/ConnectedCDPBookingForm';
import history from '../../../../core/history';
import { createLocationUrlFromLocation } from '../../../../core/url/location';
import { showChatEntrypoint } from '../../../../core/util/account';
import { isPremiumBooking } from '../../../../core/util/booking';
import { hasBoost, isProviderGroupLocation } from '../../../../core/util/location';
import { useAccount } from '../../../../hooks/account/useAccount';
import useSelectActiveUserProfile from '../../../../hooks/useSelectActiveUserProfile';
import DateTime, { HOUR_MINUTE_PERIOD_DAY_MONTH_DAY_NUMBER } from '../../../DateTime';
import usePlusChat from '../../../Membership/utils';
import ObserverContainer from '../../../SolvPatternLibrary/Profilers/Observer';
import ConnectedVisitPhotos from '../../PostVisitSummary/ConnectedVisitPhotos';
import { Actions, Events } from '../../util/analytics';
import Label, { LabelColors, LabelText } from './components/Label';
import PayBill from './components/PayBill';
import ViewResults from './components/ViewResults';
import VisitDetails from './components/VisitDetails';
import VisitNotes from './components/VisitNotes';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';

type VisitSummaryStatusProps = {
  bookingId: string;
};

const BaseClickableWithBorder = styled(BaseClickable).withConfig({ componentId: 'sc-1nmz4v-0' })`
  border-bottom: 1px solid ${colors.borderLine};
  width: 100%;
  border-radius: 0;
`;

const GrayChevronRight = styled(ChevronRight).withConfig({ componentId: 'sc-1nmz4v-1' })`
  color: ${palette.gray[400]};
`;

const dividerBorderStyle = `1px solid ${colors.borderLine}`;

export const VisitSummaryStatus = ({ bookingId }: VisitSummaryStatusProps) => {
  const { isLoading: summaryLoading, summaryStatus } = useVisitSummary(bookingId);
  const downloadedMutation = useVisitSummaryDownloadedMutation(bookingId);
  const isLoading = summaryLoading || downloadedMutation.isLoading;
  const { t } = useTranslation(['account']);

  if (isLoading && summaryStatus !== VisitSummaryStatuses.Missing) {
    return (
      <Box
        aria-label="visit summary info is loading"
        borderBottom={dividerBorderStyle}
        p={2}
        role="status"
        textAlign="center"
        width="100%"
      >
        <Spinner />
      </Box>
    );
  }

  switch (summaryStatus) {
    case VisitSummaryStatuses.Downloadable:
      return (
        <BaseClickableWithBorder
          contentProps={{ justifyContent: 'flex-start' }}
          onClick={() => {
            analyticsTrackEvent(VISIT_SUMMARY_TOUCH_DOWNLOAD, { source: 'Past visit card' });
            downloadedMutation.mutate();
          }}
          p={2}
        >
          <Text as="span" textColor="gray-500" variant="body2">
            <Trans i18nKey="account:manage.pastAppointmentCard.visitSummary.downloadPrompt" t={t}>
              <Text as="span" textColor="crunchberry" variant="body2">
                0
              </Text>
            </Trans>
          </Text>
        </BaseClickableWithBorder>
      );
    case VisitSummaryStatuses.Updatable:
      return (
        <BaseClickableWithBorder
          contentProps={{ justifyContent: 'flex-start' }}
          onClick={() => {
            analyticsTrackEvent(VISIT_SUMMARY_TOUCH_DOWNLOAD, {
              entrypoint: USER_PROFILE_PAST_APPOINTMENT_CARD,
            });
            downloadedMutation.mutate();
          }}
          p={2}
        >
          <Text as="span" textColor="gray-500" variant="body2">
            <Trans i18nKey="account:manage.pastAppointmentCard.visitSummary.updatePrompt" t={t}>
              <Text as="span" textColor="crunchberry" variant="body2">
                0
              </Text>
            </Trans>
          </Text>
        </BaseClickableWithBorder>
      );
    case VisitSummaryStatuses.Viewable:
      return (
        <BaseClickableWithBorder
          contentProps={{ width: '100%' }}
          href={`/visit-summary/${bookingId}`}
          onClick={() =>
            analyticsTrackEvent(VISIT_SUMMARY_TOUCH_VIEW, {
              entrypoint: USER_PROFILE_PAST_APPOINTMENT_CARD,
            })
          }
          p={2}
        >
          <Stack justifyContent="space-between" row width="100%">
            <Text>{t('account:manage.pastAppointmentCard.visitSummary.viewPrompt')}</Text>
            <GrayChevronRight />
          </Stack>
        </BaseClickableWithBorder>
      );
    case VisitSummaryStatuses.Pending:
      return (
        <Box borderBottom={dividerBorderStyle} p={2}>
          <Text textColor="gray-500" variant="body2">
            {t('account:manage.pastAppointmentCard.visitSummary.pending')}
          </Text>
        </Box>
      );
    case VisitSummaryStatuses.Missing:
    default:
      return null;
  }
};

type Props = {
  booking?: any;
  fetchBookingNotes: () => void;
  saveBookingNotes: (form: any, onSuccess: any) => void;
  updateBookingNotes: (form: any, onSuccess: any) => void;
  location?: Location;
  notes?: any;
  userProfileId?: string;
  className?: string;
};
const Past = (props: Props) => {
  const {
    booking,
    location,
    notes: { result },
    userProfileId,
    saveBookingNotes,
    updateBookingNotes,
    className,
    fetchBookingNotes,
  } = props;

  const dispatch = useDispatch();
  const [shownModal, setShownModal] = useState(false);
  const handleClose = () => setShownModal(false);
  const handleOpen = () => setShownModal(true);

  const flags = useLDFlags();

  const { t } = useTranslation('pass');

  const getAppointmentDate = () => {
    return DateTime.format(
      booking.appointment_date,
      booking.time_zone
    )(HOUR_MINUTE_PERIOD_DAY_MONTH_DAY_NUMBER) as string;
  };

  const shouldShowPayBill = () => !!booking.invoice_id;

  const shouldShowLabResults = () => booking.has_associated_lab_results;

  const onLeaveReviewClick = () => {
    analyticsTrackEvent(Events.PAST_CARD, {
      action: Actions.CLICK_LEAVE_REVIEW,
    });
    history.push(`/account/add-review/${booking.id}`);
  };

  const startRebooking = useRebooking(booking, handleOpen);
  const onBookAgainClick = () => {
    analyticsTrackEvent(Events.PAST_CARD, {
      action: Actions.CLICK_BOOK_AGAIN,
      platformType: location?.platformType,
      providerId: booking.providerId,
      locationId: booking.locationId,
    });
    if (!isProviderGroupLocation(location)) {
      if (flags.kis511H22022BookingFlow) {
        dispatch(setReasonForVisit(booking.reason));
      }

      const url = createLocationUrlFromLocation(location as Location);
      history.push(url, { from: 'account' });
    } else {
      startRebooking();
    }
  };

  const isPremium = isPremiumBooking(booking);

  const account = useAccount();
  const activeUserProfile = useSelectActiveUserProfile();
  const { plus673IsIntercomEnabled } = useLDFlags();

  const showConciergeChatEntrypoint = showChatEntrypoint(
    plus673IsIntercomEnabled,
    account,
    activeUserProfile,
    isPremium
  );

  const onChatClick = usePlusChat(ACCOUNT_PROFILE_PAST_VISITS_CARD);

  return (
    <ObserverContainer
      handleSawComponent={() => {
        if (!isPremium) {
          fetchBookingNotes();
        }
      }}
    >
      <Box
        backgroundColor="white"
        borderRadius="xl"
        className={className}
        color={colors.squid}
        fontSize="sm"
        maxWidth="100%"
      >
        <Box borderBottom={dividerBorderStyle}>
          <Label label={LabelText.PAST} labelColor={LabelColors.PAST} />

          <VisitDetails
            appointmentDate={getAppointmentDate()}
            booking={booking}
            collapsible={false}
            location={location}
          />
        </Box>

        <Box borderBottom={dividerBorderStyle} p={2}>
          <Text color={colors.squid} fontSize="base" variant="h2">
            Reason for visit
          </Text>
          <Box color={colors.lightText} fontSize="sm" mt="4px">
            {booking.reason || 'Not specified'}
          </Box>
        </Box>

        <VisitSummaryStatus bookingId={booking?.id} />

        {shouldShowLabResults() && (
          <Box borderBottom={dividerBorderStyle} p={2}>
            <ViewResults bookingId={booking.id} lastName={booking.last_name} />
          </Box>
        )}

        <Box borderBottom={dividerBorderStyle} p={2}>
          <Stack
            color={colors.squid}
            fontSize="base"
            justifyContent="space-between"
            onClick={onLeaveReviewClick}
            row
            style={{ cursor: 'pointer' }}
          >
            <Text>Leave a review</Text>
            <GrayChevronRight />
          </Stack>
        </Box>

        <Box borderBottom={dividerBorderStyle} p={2}>
          <Stack
            color={colors.squid}
            fontSize="base"
            justifyContent="space-between"
            onClick={onBookAgainClick}
            row
            style={{ cursor: 'pointer' }}
          >
            <Text>Book another appointment</Text>
            <GrayChevronRight />
          </Stack>
        </Box>

        {!isPremium && (
          <>
            <VisitNotes
              notes={result?.notes}
              notesResult={result}
              prescriptions={result?.prescriptions}
              saveBookingNotes={saveBookingNotes}
              updateBookingNotes={updateBookingNotes}
            />

            <Box borderBottom={dividerBorderStyle} p={2}>
              <Text color={colors.squid} fontSize="base" variant="h2">
                Photos
              </Text>

              <ConnectedVisitPhotos
                accountId={booking.account_id}
                booking={booking}
                userProfileId={booking.user_profile_id}
              />
            </Box>
          </>
        )}
        {shouldShowPayBill() && (
          <PayBill
            bookingId={booking.id}
            invoiceId={booking.invoice_id}
            userProfileId={userProfileId}
          />
        )}
        {showConciergeChatEntrypoint && (
          <BaseClickable minWidth="100%" onClick={onChatClick}>
            <span
              style={{ background: '#EEFAFC', display: 'flex', padding: '12px', minWidth: '100%' }}
            >
              <img src="/images/membership/chat_icon_blue.svg" style={{ paddingRight: '8px' }} />
              <Text as="span" fontSize="sm" pr={1}>
                {t('postActions.chat.subtitle')}
              </Text>
              <Text as="span" fontSize="sm">
                <u>{t('postActions.chat.ctaText')}</u>
              </Text>
            </span>
          </BaseClickable>
        )}
      </Box>
      {hasBoost(location) && (
        <Modal onClose={handleClose} open={shownModal}>
          <BookingProfileForm
            handleClose={handleClose}
            location={location}
            onPostSubmit={handleClose}
            previousBooking={booking}
          />
        </Modal>
      )}
    </ObserverContainer>
  );
};

export default Past;
